import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { TabView, TabPanel } from "primereact/tabview";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import "primereact/resources/primereact.min.css";
import * as api from "../../utils/Api.ts";
import type { ReporteFinanzasFiltros } from "../../utils/Api.ts";
import { showMsg } from "../../utils/Constants.tsx";
import ChartCard from "../../components/widgets/graphs/ChartCard.tsx";
import KpiCard from "../../components/widgets/graphs/KpiCard.tsx";
import MultiBarChart from "../../components/widgets/graphs/MultiBarChart.tsx";
import ComboBarLineChart from "../../components/widgets/graphs/ComboBarLineChart.tsx";
import DoughnutChart from "../../components/widgets/graphs/DoughnutChart.tsx";
import HeatMatrix from "../../components/widgets/graphs/HeatMatrix.tsx";
import {
  CHART_COLORS,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  formatPercent,
} from "../../components/widgets/graphs/chartTheme.ts";
import { useNavigate } from "react-router-dom";
import NumberBoxes, {
  NumberBoxItem,
} from "../../components/reportes/NumberBoxes.tsx";
import {
  ESTADO_PAGO_TAG,
  inetInfo,
  useScrollbarGutter,
} from "../../components/reportes/reportesUtils.ts";
import "./dashboard.css";
import "../../components/reportes/reportes.css";

/* ------------------------------------------------------------------ */
/* Types (shape returned by /finanzas/reportes/*)                      */
/* ------------------------------------------------------------------ */

interface Resumen {
  cantidadOrdenes: number;
  cantidadProveedores: number;
  ordenesSinFactura: number;
  cantidadFacturas: number;
  facturasSinComprobante: number;
  montoTotalOrdenes: number;
  montoFacturado: number;
  montoContabilizado: number;
  montoPendienteFacturar: number;
  diferenciaFacturadoContabilizado: number;
  porcentajeFacturado: number;
}

interface ProveedorRow {
  identificadorProveedor: string;
  nombreProveedor: string;
  cantidadOrdenes: number;
  cantidadFacturas: number;
  ordenesSinFactura: number;
  montoOrdenes: number;
  montoFacturado: number;
  montoContabilizado: number;
  montoPendienteFacturar: number;
  porcentajeFacturado: number;
}

interface PeriodoRow {
  periodo: string;
  cantidadFacturas: number;
  cantidadOrdenes: number;
  facturasSinComprobante: number;
  montoFacturado: number;
  montoContabilizado: number;
  diferencia: number;
}

interface CuadraturaRow {
  estadoFactura: string;
  estadoContable: string;
  cantidadFacturas: number;
  montoFacturado: number;
  montoContabilizado: number;
  diferencia: number;
}

interface TipoDocumentoRow {
  tipoDocumento: number;
  nombreDocumento: string;
  cantidadDocumentos: number;
  cantidadProveedores: number;
  montoFacturado: number;
  montoContabilizado: number;
}

interface EstadoOrdenRow {
  estadoOrden: number;
  tipoOrden: number;
  cantidadOrdenes: number;
  ordenesSinFactura: number;
  montoOrdenes: number;
  montoFacturado: number;
  montoPendienteFacturar: number;
}

interface DiferenciaFacturaRow {
  nroOrdenCompra: number;
  numeroFacturaLegal: number;
  numeroFacturaRea: number;
  identificadorProveedor: string;
  nombreProveedor: string;
  nombreDocumento: string;
  fechaFactura: string;
  estadoFactura: string;
  estadoContable: string;
  montoFacturado: number;
  montoContabilizado: number;
  diferencia: number;
}

interface OrdenSobrefacturadaRow {
  nroOrdenCompra: number;
  identificadorProveedor: string;
  nombreProveedor: string;
  cantidadFacturas: number;
  montoOrden: number;
  montoFacturado: number;
  exceso: number;
}

interface Diferencias {
  tolerancia: number;
  facturas: DiferenciaFacturaRow[];
  ordenesSobrefacturadas: OrdenSobrefacturadaRow[];
}

// /reports/pagos/resumen -> porEstadoPago (invoices by payment state)
interface EstadoPagoRow {
  estadoPago: string;
  cantidadFacturas: number;
  montoFacturado: number;
  // OC with at least one invoice in this state (what the seguimiento screen
  // lists). Optional until the backend sends it; invoices are shown meanwhile
  cantidadOrdenes?: number;
}

interface PagosResumen {
  kpis?: { cantidadFacturas: number; cantidadOrdenes?: number };
  porEstadoPago: EstadoPagoRow[];
}

interface FiltrosForm {
  fechaDesde: Date | null;
  fechaHasta: Date | null;
  campoFecha: string;
  identificadorProveedor: string;
  estadoOrden: number | null;
  tipoOrden: number | null;
  tipoDocumento: number | null;
}

/* ------------------------------------------------------------------ */
/* Catalogs (same labels as the contabilidad maintenance table)        */
/* ------------------------------------------------------------------ */

const CAMPO_FECHA_OPTIONS = [
  { label: "Fecha factura", value: "factura" },
  { label: "Fecha contable", value: "contable" },
  { label: "Fecha orden", value: "orden" },
];

// Values must match the backend AgrupacionPeriodo enum
const AGRUPACION_OPTIONS = [
  { label: "Mes", value: "mes" },
  { label: "Trimestre", value: "trimestre" },
  { label: "Año", value: "anio" },
];

const TIPO_ORDEN_OPTIONS = [
  { label: "Stock", value: 1 },
  { label: "Servicios", value: 2 },
  { label: "Activos/Inversiones", value: 3 },
];

const ESTADO_ORDEN_OPTIONS = [
  { label: "EMITIDA", value: 2 },
  { label: "PARCIALMENTE CUMPLIDA", value: 3 },
  { label: "CUMPLIDA", value: 4 },
  { label: "ANULADA", value: 5 },
];

const TIPO_DOCUMENTO_OPTIONS = [
  { label: "70 - Factura Electrónica", value: 70 },
  { label: "71 - Nota de Crédito", value: 71 },
  { label: "72 - Nota de Débito", value: 72 },
  { label: "77 - Factura Exenta", value: 77 },
];

// Big-number boxes. Values match the filters of the contabilidad maintenance
// screen (/contabilidad/seguimiento?estadoOrden=4 | ?estadoPago=PAGADA)
const RUTA_SEGUIMIENTO = "/contabilidad/seguimiento";

const ESTADOS_INET_BOXES = [
  { value: 2, titulo: "Emitidas" },
  { value: 3, titulo: "Parcialmente cumplidas" },
  { value: 4, titulo: "Cumplidas" },
  { value: 5, titulo: "Anuladas" },
];

// Colors come from ESTADO_PAGO_TAG: same as the maintenance "Estado Pago" tags
const ESTADOS_PAGO_BOXES = [
  { value: "PAGADA", titulo: "Pagadas" },
  { value: "PENDIENTE", titulo: "Pendientes" },
  { value: "PROCESADA", titulo: "Procesadas" },
  { value: "ANULADA", titulo: "Anuladas" },
];

const TOP_PROVEEDORES_LIMIT = 50;
const TOP_PROVEEDORES_CHART = 10;
// Every supplier (the backend caps limit at 5000) so the ranking by N° OC is
// not limited to the suppliers with the largest amounts
const PROVEEDORES_FETCH_LIMIT = 5000;
const DETALLE_LIMIT = 200;
// Adjustment documents: every row of /diferencias with tolerance 0 (backend
// max 5000), split client side into rebaje / nota de crédito / nota de débito
const AJUSTES_LIMIT = 5000;

// DTE type from "71 N.C.Electrónica" / "72 N.D. ..." style document names
const tipoDte = (nombreDocumento: string | null | undefined): number | null => {
  const match = /^\s*(\d+)/.exec(nombreDocumento ?? "");
  return match ? Number(match[1]) : null;
};
const esNotaCredito = (nombre: string) =>
  tipoDte(nombre) === 71 || /N\.\s?C\.|NOTA DE CR[EÉ]DITO/i.test(nombre ?? "");
const esNotaDebito = (nombre: string) =>
  tipoDte(nombre) === 72 || /N\.\s?D\.|NOTA DE D[EÉ]BITO/i.test(nombre ?? "");
const esRebaje = (estadoFactura: string | null | undefined) =>
  /REBAJE/i.test(estadoFactura ?? "");

const ESTADO_ORDEN_SEVERITY: Record<
  number,
  "info" | "warning" | "success" | "danger"
> = { 2: "info", 3: "warning", 4: "success", 5: "danger" };

const ESTADO_CUADRATURA_COLORS: Record<string, string> = {
  CUADRADO: CHART_COLORS.contabilizado,
  DESCUADRADO: CHART_COLORS.diferencia,
  "SIN ESTADO": CHART_COLORS.neutral,
};

const MESES_CORTOS = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

const labelFrom = (
  options: { label: string; value: number }[],
  value: number | null | undefined,
) =>
  options.find((o) => o.value === Number(value))?.label ??
  (value === null || value === undefined ? "SIN DATO" : String(value));

// Local date -> YYYY-MM-DD (toISOString would shift the day by timezone)
const toIsoDate = (d: Date | null): string | undefined => {
  if (!d) return undefined;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// SQL dates arrive as ISO strings at UTC midnight; read the date part as-is
const formatFecha = (value: string | null | undefined): string => {
  if (!value) return "-";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  return match ? `${match[3]}-${match[2]}-${match[1]}` : String(value);
};

// "2026-03" -> "Mar 2026"; other formats (quarter / year) are shown as-is
const formatPeriodo = (periodo: string): string => {
  const match = /^(\d{4})-(\d{1,2})$/.exec(String(periodo));
  if (match) {
    const mes = MESES_CORTOS[Number(match[2]) - 1];
    if (mes) return `${mes} ${match[1]}`;
  }
  return String(periodo);
};

const truncate = (text: string, max = 28) =>
  text && text.length > max ? `${text.substring(0, max - 1)}…` : text || "-";

const quickRanges = () => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const quarterStart = Math.floor(m / 3) * 3;
  return [
    { label: "Mes actual", desde: new Date(y, m, 1), hasta: today },
    { label: "Trimestre", desde: new Date(y, quarterStart, 1), hasta: today },
    { label: "Año actual", desde: new Date(y, 0, 1), hasta: today },
    {
      label: "Últimos 12 meses",
      desde: new Date(y, m - 11, 1),
      hasta: today,
    },
    {
      label: "Año anterior",
      desde: new Date(y - 1, 0, 1),
      hasta: new Date(y - 1, 11, 31),
    },
  ];
};

const defaultFiltros = (): FiltrosForm => {
  const today = new Date();
  return {
    fechaDesde: new Date(today.getFullYear(), 0, 1),
    fechaHasta: today,
    campoFecha: "factura",
    identificadorProveedor: "",
    estadoOrden: null,
    tipoOrden: null,
    tipoDocumento: null,
  };
};

const toQuery = (f: FiltrosForm): ReporteFinanzasFiltros => ({
  fechaDesde: toIsoDate(f.fechaDesde),
  fechaHasta: toIsoDate(f.fechaHasta),
  campoFecha: f.campoFecha,
  identificadorProveedor: f.identificadorProveedor.trim() || undefined,
  estadoOrden: f.estadoOrden ?? undefined,
  tipoOrden: f.tipoOrden ?? undefined,
  tipoDocumento: f.tipoDocumento ?? undefined,
});

const errorMessage = (error: unknown) =>
  error instanceof Error && error.message
    ? error.message
    : "No fue posible cargar el reporte";

/**
 * Loads one report and re-runs whenever `key` changes. Responses from
 * superseded requests are discarded so fast filter changes can't race.
 */
function useReporte<T>(fetcher: () => Promise<T>, key: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      if (id === requestId.current) setData(result ?? null);
    } catch (e) {
      console.error("Error cargando reporte contabilidad:", e);
      if (id === requestId.current) {
        setData(null);
        setError(errorMessage(e));
      }
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [key, load]);

  return { data, loading, error, reload: load };
}

/* ------------------------------------------------------------------ */
/* Screen                                                               */
/* ------------------------------------------------------------------ */

export default function IndexContabilidad() {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  useScrollbarGutter();
  const detalleRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState<FiltrosForm>(defaultFiltros);
  const [applied, setApplied] = useState<FiltrosForm>(defaultFiltros);
  const [proveedorNombre, setProveedorNombre] = useState<string>("");
  const [agrupacion, setAgrupacion] = useState<string>("mes");
  const [toleranciaInput, setToleranciaInput] = useState<number | null>(1);
  const [tolerancia, setTolerancia] = useState<number>(1);
  const [detalleTab, setDetalleTab] = useState(0);
  const [exporting, setExporting] = useState(false);

  const query = useMemo(() => toQuery(applied), [applied]);
  const queryKey = JSON.stringify(query);
  const isDirty = JSON.stringify(toQuery(form)) !== queryKey;

  const R = api.GET_REPORTES_FINANZAS;

  const resumen = useReporte<Resumen>(() => R.getResumen(query), queryKey);
  // All suppliers, sorted by monto OC by the backend
  const proveedores = useReporte<ProveedorRow[]>(
    () => R.getProveedores({ ...query, limit: PROVEEDORES_FETCH_LIMIT }),
    queryKey,
  );
  const periodos = useReporte<PeriodoRow[]>(
    () => R.getPeriodos({ ...query, agrupacion }),
    `${queryKey}|${agrupacion}`,
  );
  const cuadratura = useReporte<CuadraturaRow[]>(
    () => R.getEstadosCuadratura(query),
    queryKey,
  );
  const tiposDocumento = useReporte<TipoDocumentoRow[]>(
    () => R.getTiposDocumento(query),
    queryKey,
  );
  const estadosOrden = useReporte<EstadoOrdenRow[]>(
    () => R.getEstadosOrden(query),
    queryKey,
  );
  // Rebaje / nota de crédito / nota de débito listings. /diferencias with
  // tolerance 0 returns every invoice whose facturado ≠ contabilizado, which
  // includes every credit note (they are booked with the opposite sign)
  const ajustes = useReporte<Diferencias>(
    () => R.getDiferencias({ ...query, tolerancia: 0, limit: AJUSTES_LIMIT }),
    queryKey,
  );
  const diferencias = useReporte<Diferencias>(
    () => R.getDiferencias({ ...query, tolerancia, limit: DETALLE_LIMIT }),
    `${queryKey}|${tolerancia}`,
  );
  // Payment states come from the /reports API, which only shares the date
  // filters with this dashboard (RUT / estado / tipo filters don't apply)
  const pagos = useReporte<PagosResumen>(
    () =>
      api.GET_REPORTES.getPagosResumen({
        fechaDesde: query.fechaDesde,
        fechaHasta: query.fechaHasta,
        campoFecha: query.campoFecha,
      }),
    `${query.fechaDesde}|${query.fechaHasta}|${query.campoFecha}`,
  );

  const reloadAll = () => {
    [
      resumen,
      proveedores,
      periodos,
      cuadratura,
      tiposDocumento,
      estadosOrden,
      ajustes,
      diferencias,
      pagos,
    ].forEach((r) => r.reload());
  };

  /* ------------------------- filters ------------------------- */

  const applyFiltros = () => {
    if (
      form.fechaDesde &&
      form.fechaHasta &&
      form.fechaDesde > form.fechaHasta
    ) {
      showMsg(
        toast,
        "warn",
        "Filtros",
        "La fecha desde no puede ser mayor que la fecha hasta",
      );
      return;
    }
    if (form.identificadorProveedor.trim() !== applied.identificadorProveedor) {
      setProveedorNombre("");
    }
    setApplied({ ...form });
  };

  const clearFiltros = () => {
    const initial = defaultFiltros();
    setForm(initial);
    setApplied(initial);
    setProveedorNombre("");
  };

  const applyQuickRange = (desde: Date, hasta: Date) => {
    const next = { ...form, fechaDesde: desde, fechaHasta: hasta };
    setForm(next);
    setApplied(next);
  };

  const filterByProveedor = useCallback(
    (identificador: string, nombre: string) => {
      if (!identificador) return;
      setProveedorNombre(nombre);
      setForm((f) => ({ ...f, identificadorProveedor: identificador }));
      setApplied((f) => ({ ...f, identificadorProveedor: identificador }));
    },
    [],
  );

  const removeProveedor = () => {
    setProveedorNombre("");
    setForm((f) => ({ ...f, identificadorProveedor: "" }));
    setApplied((f) => ({ ...f, identificadorProveedor: "" }));
  };

  const scrollTo = (ref: RefObject<HTMLDivElement>) =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ------------------------- derived chart data ------------------------- */

  const periodoChart = useMemo(() => {
    const rows = periodos.data ?? [];
    return {
      labels: rows.map((r) => formatPeriodo(r.periodo)),
      bars: [
        {
          label: "Facturado",
          data: rows.map((r) => r.montoFacturado),
          color: CHART_COLORS.facturado,
        },
        {
          label: "Contabilizado",
          data: rows.map((r) => r.montoContabilizado),
          color: CHART_COLORS.contabilizado,
        },
      ],
      lines: [
        {
          label: "N° facturas",
          data: rows.map((r) => r.cantidadFacturas),
          color: CHART_COLORS.pendiente,
        },
      ],
    };
  }, [periodos.data]);

  // Suppliers ranked by number of OC (the amount ranking is below)
  const proveedoresPorOrdenes = useMemo(
    () =>
      [...(proveedores.data ?? [])]
        .sort((a, b) => b.cantidadOrdenes - a.cantidadOrdenes)
        .slice(0, TOP_PROVEEDORES_CHART),
    [proveedores.data],
  );

  const proveedoresOrdenesChart = useMemo(
    () => ({
      labels: proveedoresPorOrdenes.map(
        (p) => p.nombreProveedor?.trim() || p.identificadorProveedor,
      ),
      series: [
        {
          label: "N° OC",
          data: proveedoresPorOrdenes.map((p) => p.cantidadOrdenes),
          color: CHART_COLORS.ordenado,
        },
        {
          label: "N° facturas",
          data: proveedoresPorOrdenes.map((p) => p.cantidadFacturas),
          color: CHART_COLORS.facturado,
        },
      ],
    }),
    [proveedoresPorOrdenes],
  );

  const ajustesDetalle = useMemo(() => {
    const rows = ajustes.data?.facturas ?? [];
    return {
      rebajeONotaCredito: rows.filter(
        (r) => esRebaje(r.estadoFactura) || esNotaCredito(r.nombreDocumento),
      ),
      notasDebito: rows.filter((r) => esNotaDebito(r.nombreDocumento)),
      notasCreditoListadas: rows.filter((r) => esNotaCredito(r.nombreDocumento))
        .length,
      notasCreditoTotal:
        tiposDocumento.data?.find((t) => t.tipoDocumento === 71)
          ?.cantidadDocumentos ?? null,
      truncado: rows.length >= AJUSTES_LIMIT,
    };
  }, [ajustes.data, tiposDocumento.data]);

  const topProveedores = useMemo(
    () => (proveedores.data ?? []).slice(0, TOP_PROVEEDORES_CHART),
    [proveedores.data],
  );

  const proveedoresChart = useMemo(
    () => ({
      labels: topProveedores.map(
        (p) => p.nombreProveedor || p.identificadorProveedor,
      ),
      series: [
        {
          label: "Monto OC",
          data: topProveedores.map((p) => p.montoOrdenes),
          color: CHART_COLORS.ordenado,
        },
        {
          label: "Facturado",
          data: topProveedores.map((p) => p.montoFacturado),
          color: CHART_COLORS.facturado,
        },
        {
          label: "Contabilizado",
          data: topProveedores.map((p) => p.montoContabilizado),
          color: CHART_COLORS.contabilizado,
        },
      ],
    }),
    [topProveedores],
  );

  // Stable handler: an inline one would rebuild the chart options (and
  // replay its animation) on every re-render, e.g. when the agrupación changes
  const onProveedorOrdenesClick = useCallback(
    (index: number) => {
      const p = proveedoresPorOrdenes[index];
      if (p) filterByProveedor(p.identificadorProveedor, p.nombreProveedor);
    },
    [proveedoresPorOrdenes, filterByProveedor],
  );

  const onProveedorBarClick = useCallback(
    (index: number) => {
      const p = topProveedores[index];
      if (p) filterByProveedor(p.identificadorProveedor, p.nombreProveedor);
    },
    [topProveedores, filterByProveedor],
  );

  const cuadraturaChart = useMemo(() => {
    const byEstado = new Map<string, number>();
    (cuadratura.data ?? []).forEach((r) =>
      byEstado.set(
        r.estadoFactura,
        (byEstado.get(r.estadoFactura) ?? 0) + r.cantidadFacturas,
      ),
    );
    const labels = Array.from(byEstado.keys());
    return {
      labels,
      values: labels.map((l) => byEstado.get(l) ?? 0),
      colors: labels.map(
        (l) => ESTADO_CUADRATURA_COLORS[l] ?? CHART_COLORS.neutral,
      ),
      total: Array.from(byEstado.values()).reduce((a, v) => a + v, 0),
      cells: (cuadratura.data ?? []).map((r) => ({
        row: r.estadoFactura,
        col: r.estadoContable,
        value: r.cantidadFacturas,
        detail: `Facturado ${formatMoney(r.montoFacturado)} · Contabilizado ${formatMoney(
          r.montoContabilizado,
        )} · Dif. ${formatMoney(r.diferencia)}`,
      })),
    };
  }, [cuadratura.data]);

  const tiposDocumentoChart = useMemo(() => {
    const rows = tiposDocumento.data ?? [];
    return {
      labels: rows.map(
        (r) =>
          r.nombreDocumento ||
          labelFrom(TIPO_DOCUMENTO_OPTIONS, r.tipoDocumento),
      ),
      values: rows.map((r) => r.montoFacturado),
      total: rows.reduce((a, r) => a + r.montoFacturado, 0),
    };
  }, [tiposDocumento.data]);

  const estadosOrdenChart = useMemo(() => {
    const rows = estadosOrden.data ?? [];
    const estados = Array.from(new Set(rows.map((r) => r.estadoOrden)));
    const tipos = Array.from(new Set(rows.map((r) => r.tipoOrden)));
    const value = (estado: number, tipo: number) =>
      rows.find((r) => r.estadoOrden === estado && r.tipoOrden === tipo)
        ?.montoOrdenes ?? 0;
    return {
      labels: estados.map((e) => labelFrom(ESTADO_ORDEN_OPTIONS, e)),
      series: tipos.map((t, i) => ({
        label: labelFrom(TIPO_ORDEN_OPTIONS, t),
        data: estados.map((e) => value(e, t)),
        color: [
          CHART_COLORS.facturado,
          CHART_COLORS.pendiente,
          "#7c5cc4",
          CHART_COLORS.neutral,
        ][i % 4],
      })),
    };
  }, [estadosOrden.data]);

  const estadosOrdenTotales = useMemo(() => {
    const rows = estadosOrden.data ?? [];
    const byEstado = new Map<number, EstadoOrdenRow>();
    rows.forEach((r) => {
      const acc = byEstado.get(r.estadoOrden);
      byEstado.set(
        r.estadoOrden,
        acc
          ? {
              ...acc,
              cantidadOrdenes: acc.cantidadOrdenes + r.cantidadOrdenes,
              ordenesSinFactura: acc.ordenesSinFactura + r.ordenesSinFactura,
              montoOrdenes: acc.montoOrdenes + r.montoOrdenes,
              montoFacturado: acc.montoFacturado + r.montoFacturado,
              montoPendienteFacturar:
                acc.montoPendienteFacturar + r.montoPendienteFacturar,
            }
          : { ...r },
      );
    });
    return Array.from(byEstado.values());
  }, [estadosOrden.data]);

  /* ------------------------- excel ------------------------- */

  const exportExcel = async () => {
    setExporting(true);
    try {
      const xlsx = await import("xlsx");
      const wb = xlsx.utils.book_new();
      const addSheet = (name: string, rows: Record<string, unknown>[]) => {
        const sheet = xlsx.utils.json_to_sheet(
          rows.length ? rows : [{ Info: "Sin datos" }],
        );
        xlsx.utils.book_append_sheet(wb, sheet, name);
      };

      addSheet("Filtros", [
        { Filtro: "Fecha desde", Valor: query.fechaDesde ?? "-" },
        { Filtro: "Fecha hasta", Valor: query.fechaHasta ?? "-" },
        {
          Filtro: "Campo fecha",
          Valor:
            CAMPO_FECHA_OPTIONS.find((o) => o.value === query.campoFecha)
              ?.label ?? query.campoFecha,
        },
        {
          Filtro: "Proveedor",
          Valor: query.identificadorProveedor
            ? `${query.identificadorProveedor} ${proveedorNombre}`.trim()
            : "Todos",
        },
        {
          Filtro: "Estado orden",
          Valor: query.estadoOrden
            ? labelFrom(ESTADO_ORDEN_OPTIONS, query.estadoOrden)
            : "Todos",
        },
        {
          Filtro: "Tipo orden",
          Valor: query.tipoOrden
            ? labelFrom(TIPO_ORDEN_OPTIONS, query.tipoOrden)
            : "Todos",
        },
        {
          Filtro: "Tipo documento",
          Valor: query.tipoDocumento
            ? labelFrom(TIPO_DOCUMENTO_OPTIONS, query.tipoDocumento)
            : "Todos",
        },
        { Filtro: "Generado", Valor: new Date().toLocaleString("es-CL") },
      ]);

      if (resumen.data) {
        const r = resumen.data;
        addSheet("Resumen", [
          { Indicador: "Cantidad OC", Valor: r.cantidadOrdenes },
          { Indicador: "Cantidad proveedores", Valor: r.cantidadProveedores },
          { Indicador: "OC sin factura", Valor: r.ordenesSinFactura },
          { Indicador: "Cantidad facturas", Valor: r.cantidadFacturas },
          {
            Indicador: "Facturas sin comprobante",
            Valor: r.facturasSinComprobante,
          },
          { Indicador: "Monto total OC", Valor: r.montoTotalOrdenes },
          { Indicador: "Monto facturado", Valor: r.montoFacturado },
          { Indicador: "Monto contabilizado", Valor: r.montoContabilizado },
          {
            Indicador: "Monto pendiente facturar",
            Valor: r.montoPendienteFacturar,
          },
          {
            Indicador: "Diferencia facturado - contabilizado",
            Valor: r.diferenciaFacturadoContabilizado,
          },
          {
            Indicador: "% facturado",
            Valor: Number(r.porcentajeFacturado.toFixed(2)),
          },
        ]);
      }

      addSheet(
        "Proveedores",
        (proveedores.data ?? []).map((p) => ({
          RUT: p.identificadorProveedor,
          Proveedor: p.nombreProveedor,
          "N° OC": p.cantidadOrdenes,
          "N° facturas": p.cantidadFacturas,
          "OC sin factura": p.ordenesSinFactura,
          "Monto OC": p.montoOrdenes,
          Facturado: p.montoFacturado,
          Contabilizado: p.montoContabilizado,
          "Pendiente facturar": p.montoPendienteFacturar,
          "% facturado": Number(p.porcentajeFacturado.toFixed(2)),
        })),
      );

      addSheet(
        "Periodos",
        (periodos.data ?? []).map((p) => ({
          Periodo: p.periodo,
          "N° facturas": p.cantidadFacturas,
          "N° OC": p.cantidadOrdenes,
          "Facturas sin comprobante": p.facturasSinComprobante,
          Facturado: p.montoFacturado,
          Contabilizado: p.montoContabilizado,
          Diferencia: p.diferencia,
        })),
      );

      addSheet(
        "Cuadratura",
        (cuadratura.data ?? []).map((c) => ({
          "Estado factura": c.estadoFactura,
          "Estado contable": c.estadoContable,
          "N° facturas": c.cantidadFacturas,
          Facturado: c.montoFacturado,
          Contabilizado: c.montoContabilizado,
          Diferencia: c.diferencia,
        })),
      );

      addSheet(
        "Tipos documento",
        (tiposDocumento.data ?? []).map((t) => ({
          Tipo: t.tipoDocumento,
          Documento: t.nombreDocumento,
          "N° documentos": t.cantidadDocumentos,
          "N° proveedores": t.cantidadProveedores,
          Facturado: t.montoFacturado,
          Contabilizado: t.montoContabilizado,
        })),
      );

      addSheet(
        "Estados orden",
        (estadosOrden.data ?? []).map((e) => ({
          "Estado orden": labelFrom(ESTADO_ORDEN_OPTIONS, e.estadoOrden),
          "Tipo orden": labelFrom(TIPO_ORDEN_OPTIONS, e.tipoOrden),
          "N° OC": e.cantidadOrdenes,
          "OC sin factura": e.ordenesSinFactura,
          "Monto OC": e.montoOrdenes,
          Facturado: e.montoFacturado,
          "Pendiente facturar": e.montoPendienteFacturar,
        })),
      );

      const ajusteRow = (d: DiferenciaFacturaRow) => ({
        "N° OC": d.nroOrdenCompra,
        "Factura legal": d.numeroFacturaLegal,
        "Factura REA": d.numeroFacturaRea,
        RUT: d.identificadorProveedor,
        Proveedor: d.nombreProveedor,
        Documento: d.nombreDocumento,
        "Fecha factura": formatFecha(d.fechaFactura),
        "Estado factura": d.estadoFactura,
        "Estado pago": d.estadoContable,
        Facturado: d.montoFacturado,
        Contabilizado: d.montoContabilizado,
      });
      addSheet("Rebaje o NC", ajustesDetalle.rebajeONotaCredito.map(ajusteRow));
      addSheet("Notas de debito", ajustesDetalle.notasDebito.map(ajusteRow));

      addSheet(
        "Dif facturas",
        (diferencias.data?.facturas ?? []).map((d) => ({
          "N° OC": d.nroOrdenCompra,
          "Factura legal": d.numeroFacturaLegal,
          "Factura REA": d.numeroFacturaRea,
          RUT: d.identificadorProveedor,
          Proveedor: d.nombreProveedor,
          Documento: d.nombreDocumento,
          "Fecha factura": formatFecha(d.fechaFactura),
          "Estado factura": d.estadoFactura,
          "Estado contable": d.estadoContable,
          Facturado: d.montoFacturado,
          Contabilizado: d.montoContabilizado,
          Diferencia: d.diferencia,
        })),
      );

      addSheet(
        "OC sobrefacturadas",
        (diferencias.data?.ordenesSobrefacturadas ?? []).map((o) => ({
          "N° OC": o.nroOrdenCompra,
          RUT: o.identificadorProveedor,
          Proveedor: o.nombreProveedor,
          "N° facturas": o.cantidadFacturas,
          "Monto OC": o.montoOrden,
          Facturado: o.montoFacturado,
          Exceso: o.exceso,
        })),
      );

      addSheet(
        "Estado pago",
        (pagos.data?.porEstadoPago ?? []).map((p) => ({
          "Estado pago": p.estadoPago,
          "N° facturas": p.cantidadFacturas,
          Facturado: p.montoFacturado,
        })),
      );

      const stamp = new Date().toLocaleDateString("es-CL").split("-").join("");
      xlsx.writeFile(wb, `Reporte_Contabilidad_${stamp}.xlsx`);
    } catch (e) {
      console.error("Error exportando reporte contabilidad:", e);
      showMsg(toast, "error", "Error", "No fue posible generar el Excel");
    } finally {
      setExporting(false);
    }
  };

  /* ------------------------- table templates ------------------------- */

  const moneyBody =
    <T,>(field: keyof T) =>
    (row: T) => (
      <span className="fd-num">{formatMoney(row[field] as number)}</span>
    );

  const diffBody =
    <T,>(field: keyof T) =>
    (row: T) => {
      const v = row[field] as number;
      return (
        <span className={`fd-num ${v > 0 ? "fd-neg" : v < 0 ? "fd-pos" : ""}`}>
          {formatMoney(v)}
        </span>
      );
    };

  const estadoOrdenBody = (row: { estadoOrden: number }) => (
    <Tag
      value={labelFrom(ESTADO_ORDEN_OPTIONS, row.estadoOrden)}
      severity={ESTADO_ORDEN_SEVERITY[row.estadoOrden] ?? undefined}
    />
  );

  const estadoCuadraturaBody = (value: string) => (
    <Tag
      value={value || "SIN ESTADO"}
      severity={
        value === "CUADRADO"
          ? "success"
          : value === "DESCUADRADO"
            ? "danger"
            : "secondary"
      }
    />
  );

  const proveedorBody = (row: {
    identificadorProveedor: string;
    nombreProveedor: string;
  }) => (
    <button
      type="button"
      className="fd-link"
      title="Filtrar dashboard por este proveedor"
      onClick={() =>
        filterByProveedor(row.identificadorProveedor, row.nombreProveedor)
      }
    >
      <span className="fd-prov-name">{row.nombreProveedor || "-"}</span>
      <span className="fd-prov-rut">{row.identificadorProveedor}</span>
    </button>
  );

  const porcentajeBody = (row: ProveedorRow) => {
    const pct = Math.max(0, Math.min(100, row.porcentajeFacturado));
    return (
      <div className="fd-pct">
        <div className="fd-pct-bar">
          <div
            className="fd-pct-fill"
            style={{
              width: `${pct}%`,
              backgroundColor:
                row.porcentajeFacturado > 100.5
                  ? CHART_COLORS.diferencia
                  : CHART_COLORS.facturado,
            }}
          />
        </div>
        <span className="fd-num">{formatPercent(row.porcentajeFacturado)}</span>
      </div>
    );
  };

  // Payment state tag, same colors as the maintenance table
  const estadoPagoBody = (value: string) => {
    const estado = (value ?? "").trim();
    return estado ? (
      <Tag value={estado} severity={ESTADO_PAGO_TAG[estado]?.severity} />
    ) : (
      "-"
    );
  };

  // Rebaje / nota de crédito / nota de débito listings
  const ajustesTabla = (rows: DiferenciaFacturaRow[], emptyMessage: string) =>
    ajustes.error ? (
      <ChartCard
        title=""
        error={ajustes.error}
        onRetry={ajustes.reload}
        height={120}
      />
    ) : (
      <DataTable
        value={rows}
        loading={ajustes.loading}
        size="small"
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sortMode="single"
        removableSort
        stripedRows
        emptyMessage={emptyMessage}
        className="fd-table"
      >
        <Column
          header="N° OC"
          field="nroOrdenCompra"
          sortable
          bodyClassName="fd-num"
        />
        <Column
          header="Factura"
          field="numeroFacturaLegal"
          sortable
          body={(d: DiferenciaFacturaRow) => (
            <span className="fd-num">
              {d.numeroFacturaLegal || "-"}
              {d.numeroFacturaRea ? (
                <span className="fd-sub"> REA {d.numeroFacturaRea}</span>
              ) : null}
            </span>
          )}
        />
        <Column
          header="Proveedor"
          field="nombreProveedor"
          body={proveedorBody}
          sortable
        />
        <Column
          header="Documento"
          field="nombreDocumento"
          body={(d: DiferenciaFacturaRow) => truncate(d.nombreDocumento, 22)}
          sortable
        />
        <Column
          header="Fecha"
          field="fechaFactura"
          body={(d: DiferenciaFacturaRow) => formatFecha(d.fechaFactura)}
          sortable
        />
        <Column
          header="Estado factura"
          field="estadoFactura"
          body={(d: DiferenciaFacturaRow) =>
            estadoCuadraturaBody(d.estadoFactura)
          }
          sortable
        />
        <Column
          header="Estado pago"
          field="estadoContable"
          body={(d: DiferenciaFacturaRow) => estadoPagoBody(d.estadoContable)}
          sortable
        />
        <Column
          header="Facturado"
          field="montoFacturado"
          body={moneyBody<DiferenciaFacturaRow>("montoFacturado")}
          sortable
        />
        <Column
          header="Contabilizado"
          field="montoContabilizado"
          body={moneyBody<DiferenciaFacturaRow>("montoContabilizado")}
          sortable
        />
      </DataTable>
    );

  /* ------------------------- render ------------------------- */

  const r = resumen.data;
  const pctContabilizado =
    r && r.montoFacturado ? (r.montoContabilizado / r.montoFacturado) * 100 : 0;
  const campoFechaLabel =
    CAMPO_FECHA_OPTIONS.find((o) => o.value === applied.campoFecha)?.label ??
    "";
  const anyLoading = [
    resumen,
    proveedores,
    periodos,
    cuadratura,
    tiposDocumento,
    estadosOrden,
    ajustes,
    diferencias,
    pagos,
  ].some((rep) => rep.loading);
  const rangos = quickRanges();
  const rangoActivo = rangos.find(
    (q) =>
      toIsoDate(q.desde) === query.fechaDesde &&
      toIsoDate(q.hasta) === query.fechaHasta,
  )?.label;
  const filtrosActivos = [
    applied.identificadorProveedor,
    applied.estadoOrden,
    applied.tipoOrden,
    applied.tipoDocumento,
  ].filter((v) => v !== null && v !== "").length;

  /* ------------------------- big numbers ------------------------- */

  const abrirSeguimiento = (param?: string, value?: string | number) =>
    navigate(
      param
        ? `${RUTA_SEGUIMIENTO}?${param}=${encodeURIComponent(value ?? "")}`
        : RUTA_SEGUIMIENTO,
    );
  const notaSeguimiento =
    "El seguimiento no filtra por fechas: puede listar más registros";

  const inetBoxes: NumberBoxItem[] = [
    {
      key: "TOTAL",
      titulo: "Totales",
      color: "var(--primary-app-color)",
      value: estadosOrdenTotales.reduce((a, e) => a + e.cantidadOrdenes, 0),
      hint: `Ver todas las OC en seguimiento. ${notaSeguimiento}`,
      onClick: () => abrirSeguimiento(),
    },
    ...ESTADOS_INET_BOXES.map((b) => ({
      key: String(b.value),
      titulo: b.titulo,
      color: inetInfo(b.value).color,
      value:
        estadosOrdenTotales.find((e) => e.estadoOrden === b.value)
          ?.cantidadOrdenes ?? 0,
      hint: `Ver OC "${labelFrom(ESTADO_ORDEN_OPTIONS, b.value)}" en seguimiento. ${notaSeguimiento}`,
      onClick: () => abrirSeguimiento("estadoOrden", b.value),
    })),
  ];

  const pagoRows = pagos.data?.porEstadoPago ?? [];
  // Seguimiento lists OC (one row per order, even with several invoices), so
  // the boxes count OC when the backend provides it, invoices otherwise
  const pagoPorOrden =
    pagoRows.length > 0 &&
    pagoRows.every((r) => typeof r.cantidadOrdenes === "number") &&
    typeof pagos.data?.kpis?.cantidadOrdenes === "number";
  const pagoCantidad = (r: EstadoPagoRow | undefined) =>
    (pagoPorOrden ? r?.cantidadOrdenes : r?.cantidadFacturas) ?? 0;
  const pagoUnidad = pagoPorOrden ? "OC" : "facturas";
  const pagoBoxes: NumberBoxItem[] = [
    {
      key: "TOTAL",
      titulo: "Totales",
      color: "var(--primary-app-color)",
      value: pagoPorOrden
        ? (pagos.data?.kpis?.cantidadOrdenes ?? 0)
        : pagoRows.reduce((a, r) => a + r.cantidadFacturas, 0),
      hint: `Ver todas las OC en seguimiento. ${notaSeguimiento}`,
      onClick: () => abrirSeguimiento(),
    },
    ...ESTADOS_PAGO_BOXES.map((b) => ({
      key: b.value,
      titulo: b.titulo,
      color: ESTADO_PAGO_TAG[b.value]?.color ?? CHART_COLORS.neutral,
      value: pagoCantidad(pagoRows.find((r) => r.estadoPago === b.value)),
      hint: pagoPorOrden
        ? `Ver OC con facturas "${b.value}" en seguimiento. ${notaSeguimiento}`
        : `N° de facturas "${b.value}". El seguimiento lista OC con al menos una factura en este estado (una OC puede tener varias facturas). ${notaSeguimiento}`,
      onClick: () => abrirSeguimiento("estadoPago", b.value),
    })),
  ];

  return (
    <div className="fd-dashboard rp-dashboard">
      <Toast ref={toast} />

      {/* ------------------------- HEADER ------------------------- */}
      <header className="fd-hero">
        <div className="fd-hero-main">
          <div className="fd-hero-icon" aria-hidden="true">
            <i className="pi pi-chart-bar" />
          </div>
          <div className="fd-hero-text">
            <span className="fd-eyebrow">Contabilidad · Finanzas</span>
            <h4 className="fd-title">Reportes financieros</h4>
            <div className="fd-meta">
              <span className="fd-meta-item">
                <i className="pi pi-calendar" />
                {formatFecha(query.fechaDesde)}
                <i className="pi pi-arrow-right fd-meta-arrow" />
                {formatFecha(query.fechaHasta)}
              </span>
              <span className="fd-meta-item">
                <i className="pi pi-clock" />
                Por {campoFechaLabel.toLowerCase()}
              </span>
              {filtrosActivos > 0 ? (
                <span className="fd-meta-item fd-meta-accent">
                  <i className="pi pi-filter-fill" />
                  {filtrosActivos}{" "}
                  {filtrosActivos === 1 ? "filtro activo" : "filtros activos"}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="fd-hero-actions">
          <Button
            type="button"
            icon="pi pi-refresh"
            label="Actualizar"
            className="fd-btn fd-btn-ghost"
            loading={anyLoading}
            onClick={reloadAll}
          />
          <Button
            type="button"
            icon="pi pi-file-excel"
            label="Exportar Excel"
            className="fd-btn fd-btn-excel"
            loading={exporting}
            disabled={resumen.loading}
            onClick={exportExcel}
          />
        </div>
      </header>

      {/* ------------------------- FILTROS ------------------------- */}
      <section className="fd-filters" aria-label="Filtros del reporte">
        <div className="fd-filters-head">
          <span className="fd-filters-title">
            <i className="pi pi-sliders-h" />
            Filtros
          </span>
          {isDirty ? (
            <span className="fd-dirty">
              <span className="fd-dirty-dot" />
              Cambios sin aplicar
            </span>
          ) : null}
        </div>

        <div className="fd-filter-grid">
          <div className="fd-field">
            <label htmlFor="fd-desde">Desde</label>
            <Calendar
              inputId="fd-desde"
              value={form.fechaDesde}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  fechaDesde: (e.value as Date) ?? null,
                }))
              }
              dateFormat="dd/mm/yy"
              placeholder="dd/mm/aaaa"
              showIcon
              showButtonBar
              maxDate={form.fechaHasta ?? undefined}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="fd-hasta">Hasta</label>
            <Calendar
              inputId="fd-hasta"
              value={form.fechaHasta}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  fechaHasta: (e.value as Date) ?? null,
                }))
              }
              dateFormat="dd/mm/yy"
              placeholder="dd/mm/aaaa"
              showIcon
              showButtonBar
              minDate={form.fechaDesde ?? undefined}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="fd-campo">Rango aplicado a</label>
            <Dropdown
              inputId="fd-campo"
              value={form.campoFecha}
              options={CAMPO_FECHA_OPTIONS}
              onChange={(e) => setForm((f) => ({ ...f, campoFecha: e.value }))}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="fd-prov">RUT proveedor</label>
            <span className="fd-input-icon">
              <i className="pi pi-search" />
              <InputText
                id="fd-prov"
                value={form.identificadorProveedor}
                placeholder="Todos"
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    identificadorProveedor: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyFiltros();
                }}
              />
            </span>
          </div>
          <div className="fd-field">
            <label htmlFor="fd-estado">Estado orden</label>
            <Dropdown
              inputId="fd-estado"
              value={form.estadoOrden}
              options={ESTADO_ORDEN_OPTIONS}
              placeholder="Todos"
              showClear
              onChange={(e) =>
                setForm((f) => ({ ...f, estadoOrden: e.value ?? null }))
              }
            />
          </div>
          <div className="fd-field">
            <label htmlFor="fd-tipo">Tipo orden</label>
            <Dropdown
              inputId="fd-tipo"
              value={form.tipoOrden}
              options={TIPO_ORDEN_OPTIONS}
              placeholder="Todos"
              showClear
              onChange={(e) =>
                setForm((f) => ({ ...f, tipoOrden: e.value ?? null }))
              }
            />
          </div>
          <div className="fd-field">
            <label htmlFor="fd-doc">Tipo documento</label>
            <Dropdown
              inputId="fd-doc"
              value={form.tipoDocumento}
              options={TIPO_DOCUMENTO_OPTIONS}
              placeholder="Todos"
              showClear
              onChange={(e) =>
                setForm((f) => ({ ...f, tipoDocumento: e.value ?? null }))
              }
            />
          </div>
        </div>

        <div className="fd-filters-foot">
          <div className="fd-quick" role="group" aria-label="Rangos rápidos">
            <span className="fd-quick-label">Rangos rápidos</span>
            {rangos.map((q) => (
              <button
                key={q.label}
                type="button"
                className={`fd-pill${rangoActivo === q.label ? " is-active" : ""}`}
                aria-pressed={rangoActivo === q.label}
                onClick={() => applyQuickRange(q.desde, q.hasta)}
              >
                {q.label}
              </button>
            ))}
          </div>
          <div className="fd-filter-actions">
            {applied.identificadorProveedor ? (
              <span className="fd-chip">
                <i className="pi pi-building" />
                <span className="fd-chip-text">
                  {proveedorNombre
                    ? `${proveedorNombre} · ${applied.identificadorProveedor}`
                    : applied.identificadorProveedor}
                </span>
                <button
                  type="button"
                  className="fd-chip-close"
                  title="Quitar filtro de proveedor"
                  aria-label="Quitar filtro de proveedor"
                  onClick={removeProveedor}
                >
                  <i className="pi pi-times" />
                </button>
              </span>
            ) : null}
            <Button
              type="button"
              label="Limpiar"
              icon="pi pi-filter-slash"
              className="fd-btn fd-btn-subtle"
              onClick={clearFiltros}
            />
            <Button
              type="button"
              label="Aplicar filtros"
              icon="pi pi-check"
              className={`fd-btn fd-btn-primary${isDirty ? " is-dirty" : ""}`}
              onClick={applyFiltros}
            />
          </div>
        </div>
      </section>

      {/* ------------------------- BIG NUMBERS ------------------------- */}
      <NumberBoxes
        titulo="Estado INET de las órdenes de compra · N° de OC"
        items={inetBoxes}
        loading={estadosOrden.loading}
        error={estadosOrden.error}
      />
      <NumberBoxes
        titulo={`Estado de pago de las facturas · N° de ${pagoUnidad}`}
        nota={
          filtrosActivos > 0
            ? "Considera solo el rango de fechas (no aplica RUT, estado, tipo ni documento)"
            : undefined
        }
        items={pagoBoxes}
        loading={pagos.loading}
        error={pagos.error}
      />

      {/* ------------------------- KPIs ------------------------- */}
      {resumen.error ? (
        <ChartCard
          title="Resumen"
          error={resumen.error}
          onRetry={resumen.reload}
          height={80}
        />
      ) : (
        <div className="fd-kpis">
          <KpiCard
            title="Monto órdenes de compra"
            icon="pi-shopping-cart"
            color={CHART_COLORS.ordenado}
            loading={resumen.loading}
            value={formatMoney(r?.montoTotalOrdenes)}
            hint={`${formatNumber(r?.cantidadOrdenes)} OC · ${formatNumber(
              r?.cantidadProveedores,
            )} proveedores`}
          />
          <KpiCard
            title="Monto facturado"
            icon="pi-file"
            color={CHART_COLORS.facturado}
            loading={resumen.loading}
            value={formatMoney(r?.montoFacturado)}
            progress={r?.porcentajeFacturado ?? 0}
            hint={`${formatPercent(r?.porcentajeFacturado)} del monto OC · ${formatNumber(
              r?.cantidadFacturas,
            )} facturas`}
          />
          <KpiCard
            title="Monto contabilizado"
            icon="pi-book"
            color={CHART_COLORS.contabilizado}
            loading={resumen.loading}
            value={formatMoney(r?.montoContabilizado)}
            progress={pctContabilizado}
            hint={`${formatPercent(pctContabilizado)} de lo facturado`}
          />
          <KpiCard
            title="Pendiente por facturar"
            icon="pi-hourglass"
            color={CHART_COLORS.pendiente}
            tone={(r?.montoPendienteFacturar ?? 0) < 0 ? "danger" : "warning"}
            loading={resumen.loading}
            value={formatMoney(r?.montoPendienteFacturar)}
            hint={
              (r?.montoPendienteFacturar ?? 0) < 0
                ? "Facturado supera el monto OC"
                : "Monto OC − facturado"
            }
          />
          <KpiCard
            title="Dif. facturado vs contabilizado"
            icon="pi-arrows-h"
            color={CHART_COLORS.diferencia}
            tone={
              Math.abs(r?.diferenciaFacturadoContabilizado ?? 0) >= 1
                ? "danger"
                : "success"
            }
            loading={resumen.loading}
            value={formatMoney(r?.diferenciaFacturadoContabilizado)}
            hint="Ver detalle de diferencias"
            onClick={() => {
              setDetalleTab(1);
              scrollTo(detalleRef);
            }}
          />
          <KpiCard
            title="OC sin factura"
            icon="pi-exclamation-circle"
            color={CHART_COLORS.pendiente}
            tone={(r?.ordenesSinFactura ?? 0) > 0 ? "warning" : "success"}
            loading={resumen.loading}
            value={formatNumber(r?.ordenesSinFactura)}
            progress={
              r?.cantidadOrdenes
                ? (r.ordenesSinFactura / r.cantidadOrdenes) * 100
                : 0
            }
            hint="OC del período sin facturas asociadas"
          />
          <KpiCard
            title="Facturas sin comprobante"
            icon="pi-clock"
            color={CHART_COLORS.diferencia}
            tone={(r?.facturasSinComprobante ?? 0) > 0 ? "danger" : "success"}
            loading={resumen.loading}
            value={formatNumber(r?.facturasSinComprobante)}
            progress={
              r?.cantidadFacturas
                ? (r.facturasSinComprobante / r.cantidadFacturas) * 100
                : 0
            }
            hint="Facturas sin comprobante contable"
          />
          <KpiCard
            title="Proveedores"
            icon="pi-building"
            color={CHART_COLORS.neutral}
            loading={resumen.loading}
            value={formatNumber(r?.cantidadProveedores)}
            hint={`${formatNumber(r?.cantidadFacturas)} facturas en el período`}
          />
        </div>
      )}

      {/* ------------------------- PERIODO + PROVEEDORES POR N° OC ------------------------- */}
      <div className="fd-grid fd-grid-2-1">
        <ChartCard
          title="Evolución facturado vs contabilizado"
          subtitle={`Agrupado por ${campoFechaLabel.toLowerCase()}`}
          loading={periodos.loading}
          error={periodos.error}
          onRetry={periodos.reload}
          empty={!periodos.data?.length}
          actions={
            <SelectButton
              value={agrupacion}
              options={AGRUPACION_OPTIONS}
              onChange={(e) => e.value && setAgrupacion(e.value)}
              className="fd-select-sm"
              allowEmpty={false}
            />
          }
        >
          <ComboBarLineChart
            labels={periodoChart.labels}
            bars={periodoChart.bars}
            lines={periodoChart.lines}
          />
        </ChartCard>

        <ChartCard
          title={`Top ${TOP_PROVEEDORES_CHART} proveedores por N° de OC`}
          subtitle="Clic en una barra para filtrar el dashboard por ese proveedor"
          loading={proveedores.loading}
          error={proveedores.error}
          onRetry={proveedores.reload}
          empty={!proveedoresPorOrdenes.length}
        >
          <MultiBarChart
            labels={proveedoresOrdenesChart.labels}
            series={proveedoresOrdenesChart.series}
            horizontal
            money={false}
            maxLabelLength={18}
            onBarClick={onProveedorOrdenesClick}
          />
        </ChartCard>
      </div>

      {/* ------------------------- PROVEEDORES ------------------------- */}
      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title={`Top ${TOP_PROVEEDORES_CHART} proveedores por monto OC`}
          subtitle="Clic en una barra para filtrar el dashboard por ese proveedor"
          loading={proveedores.loading}
          error={proveedores.error}
          onRetry={proveedores.reload}
          empty={!topProveedores.length}
          height={420}
        >
          <MultiBarChart
            labels={proveedoresChart.labels}
            series={proveedoresChart.series}
            horizontal
            height={420}
            onBarClick={onProveedorBarClick}
          />
        </ChartCard>

        <ChartCard
          title="Detalle por proveedor"
          subtitle={`Hasta ${TOP_PROVEEDORES_LIMIT} proveedores con mayor monto OC`}
          loading={proveedores.loading}
          error={proveedores.error}
          onRetry={proveedores.reload}
          height={420}
        >
          <DataTable
            value={(proveedores.data ?? []).slice(0, TOP_PROVEEDORES_LIMIT)}
            size="small"
            scrollable
            scrollHeight="420px"
            sortMode="single"
            removableSort
            stripedRows
            emptyMessage="Sin proveedores para los filtros seleccionados"
            className="fd-table"
          >
            <Column
              header="Proveedor"
              field="nombreProveedor"
              body={proveedorBody}
              sortable
              style={{ minWidth: "12rem" }}
            />
            <Column
              header="OC"
              field="cantidadOrdenes"
              sortable
              bodyClassName="fd-num"
            />
            <Column
              header="Monto OC"
              field="montoOrdenes"
              sortable
              body={moneyBody<ProveedorRow>("montoOrdenes")}
            />
            <Column
              header="Pendiente"
              field="montoPendienteFacturar"
              sortable
              body={moneyBody<ProveedorRow>("montoPendienteFacturar")}
            />
            <Column
              header="% Facturado"
              field="porcentajeFacturado"
              sortable
              body={porcentajeBody}
              style={{ minWidth: "9rem" }}
            />
          </DataTable>
        </ChartCard>
      </div>

      {/* ------------------------- CUADRATURA / DOCUMENTOS / ESTADOS ------------------------- */}
      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title="Estado de cuadratura de facturas"
          subtitle="Cuadratura factura (filas) vs cuadratura contable (columnas) · N° facturas"
          loading={cuadratura.loading}
          error={cuadratura.error}
          onRetry={cuadratura.reload}
          empty={!cuadratura.data?.length}
          height={280}
        >
          <div className="fd-split">
            <div className="fd-split-chart">
              <DoughnutChart
                labels={cuadraturaChart.labels}
                values={cuadraturaChart.values}
                colors={cuadraturaChart.colors}
                height={250}
                centerLabel={formatNumber(cuadraturaChart.total)}
                centerCaption="facturas"
              />
            </div>
            <div className="fd-split-table">
              <HeatMatrix
                cells={cuadraturaChart.cells}
                rowTitle="Factura"
                colTitle="Contable"
                formatValue={formatNumber}
              />
            </div>
          </div>
        </ChartCard>

        <ChartCard
          title="Facturación por tipo de documento"
          subtitle="Monto facturado por tipo de DTE"
          loading={tiposDocumento.loading}
          error={tiposDocumento.error}
          onRetry={tiposDocumento.reload}
          empty={!tiposDocumento.data?.length}
          height={280}
        >
          <div className="fd-split">
            <div className="fd-split-chart">
              <DoughnutChart
                labels={tiposDocumentoChart.labels}
                values={tiposDocumentoChart.values}
                money
                height={250}
                centerLabel={formatMoneyCompact(tiposDocumentoChart.total)}
                centerCaption="facturado"
              />
            </div>
            <div className="fd-split-table">
              <DataTable
                value={tiposDocumento.data ?? []}
                size="small"
                className="fd-table"
              >
                <Column
                  header="Documento"
                  body={(t: TipoDocumentoRow) =>
                    t.nombreDocumento ||
                    labelFrom(TIPO_DOCUMENTO_OPTIONS, t.tipoDocumento)
                  }
                />
                <Column
                  header="Cant."
                  field="cantidadDocumentos"
                  bodyClassName="fd-num"
                />
                <Column
                  header="Facturado"
                  body={moneyBody<TipoDocumentoRow>("montoFacturado")}
                />
                <Column
                  header="Contabilizado"
                  body={moneyBody<TipoDocumentoRow>("montoContabilizado")}
                />
              </DataTable>
            </div>
          </div>
        </ChartCard>
      </div>

      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title="Monto OC por estado y tipo de orden"
          subtitle="Apilado por tipo de orden"
          loading={estadosOrden.loading}
          error={estadosOrden.error}
          onRetry={estadosOrden.reload}
          empty={!estadosOrden.data?.length}
        >
          <MultiBarChart
            labels={estadosOrdenChart.labels}
            series={estadosOrdenChart.series}
            stacked
          />
        </ChartCard>

        <ChartCard
          title="Resumen por estado de orden"
          subtitle="Facturado y pendiente por facturar"
          loading={estadosOrden.loading}
          error={estadosOrden.error}
          onRetry={estadosOrden.reload}
          empty={!estadosOrden.data?.length}
        >
          <DataTable
            value={estadosOrdenTotales}
            size="small"
            className="fd-table"
          >
            <Column header="Estado" body={estadoOrdenBody} />
            <Column
              header="OC"
              field="cantidadOrdenes"
              bodyClassName="fd-num"
            />
            <Column
              header="Sin factura"
              field="ordenesSinFactura"
              bodyClassName="fd-num"
            />
            <Column
              header="Monto OC"
              body={moneyBody<EstadoOrdenRow>("montoOrdenes")}
            />
            <Column
              header="Facturado"
              body={moneyBody<EstadoOrdenRow>("montoFacturado")}
            />
            <Column
              header="Pendiente"
              body={moneyBody<EstadoOrdenRow>("montoPendienteFacturar")}
            />
          </DataTable>
        </ChartCard>
      </div>

      {/* ------------------------- DETALLE ------------------------- */}
      <div ref={detalleRef} className="fd-anchor">
        <ChartCard
          title="Detalle para seguimiento"
          subtitle={`Diferencias y sobrefacturadas: hasta ${DETALLE_LIMIT} registros, ordenados por monto`}
          actions={
            detalleTab === 1 || detalleTab === 2 ? (
              <div className="fd-tolerancia">
                <label htmlFor="fd-tol">Tolerancia $</label>
                <InputNumber
                  inputId="fd-tol"
                  value={toleranciaInput}
                  onValueChange={(e) => setToleranciaInput(e.value ?? 0)}
                  min={0}
                  locale="es-CL"
                  inputClassName="p-inputtext-sm"
                  inputStyle={{ width: "7rem" }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setTolerancia(toleranciaInput ?? 0);
                  }}
                />
                <Button
                  icon="pi pi-check"
                  size="small"
                  text
                  title="Aplicar tolerancia"
                  onClick={() => setTolerancia(toleranciaInput ?? 0)}
                />
              </div>
            ) : null
          }
        >
          <TabView
            activeIndex={detalleTab}
            onTabChange={(e) => setDetalleTab(e.index)}
            className="fd-tabs"
          >
            <TabPanel
              header={`Rebaje o nota de crédito (${formatNumber(
                ajustesDetalle.rebajeONotaCredito.length,
              )})`}
            >
              <div className="fd-note">
                Facturas en estado &quot;con rebaje&quot; y notas de crédito
                (DTE 71), por fecha de {campoFechaLabel.toLowerCase()}.
                {ajustesDetalle.notasCreditoTotal !== null &&
                ajustesDetalle.notasCreditoListadas <
                  ajustesDetalle.notasCreditoTotal ? (
                  <strong>
                    {" "}
                    Se listan{" "}
                    {formatNumber(ajustesDetalle.notasCreditoListadas)} de{" "}
                    {formatNumber(ajustesDetalle.notasCreditoTotal)} notas de
                    crédito (las que no tienen diferencia contable no se
                    incluyen).
                  </strong>
                ) : null}
                {ajustesDetalle.truncado ? (
                  <strong>
                    {" "}
                    Listado truncado en {formatNumber(AJUSTES_LIMIT)}{" "}
                    documentos: acote el rango de fechas.
                  </strong>
                ) : null}
              </div>
              {ajustesTabla(
                ajustesDetalle.rebajeONotaCredito,
                "No hay facturas con rebaje ni notas de crédito para los filtros seleccionados",
              )}
            </TabPanel>

            <TabPanel
              header={`Facturas con diferencias (${formatNumber(
                diferencias.data?.facturas?.length,
              )})`}
            >
              <div className="fd-note">
                Facturas cuyo monto facturado difiere del monto contabilizado en
                más de {formatMoney(tolerancia)}.
              </div>
              {diferencias.error ? (
                <ChartCard
                  title=""
                  error={diferencias.error}
                  onRetry={diferencias.reload}
                  height={120}
                />
              ) : (
                <DataTable
                  value={diferencias.data?.facturas ?? []}
                  loading={diferencias.loading}
                  size="small"
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sortMode="single"
                  removableSort
                  stripedRows
                  emptyMessage="No hay facturas con diferencias"
                  className="fd-table"
                >
                  <Column
                    header="N° OC"
                    field="nroOrdenCompra"
                    sortable
                    bodyClassName="fd-num"
                  />
                  <Column
                    header="Factura"
                    field="numeroFacturaLegal"
                    sortable
                    body={(d: DiferenciaFacturaRow) => (
                      <span className="fd-num">
                        {d.numeroFacturaLegal || "-"}
                        {d.numeroFacturaRea ? (
                          <span className="fd-sub">
                            {" "}
                            REA {d.numeroFacturaRea}
                          </span>
                        ) : null}
                      </span>
                    )}
                  />
                  <Column
                    header="Proveedor"
                    field="nombreProveedor"
                    body={proveedorBody}
                    sortable
                  />
                  <Column
                    header="Documento"
                    field="nombreDocumento"
                    body={(d: DiferenciaFacturaRow) =>
                      truncate(d.nombreDocumento, 22)
                    }
                  />
                  <Column
                    header="Fecha"
                    field="fechaFactura"
                    body={(d: DiferenciaFacturaRow) =>
                      formatFecha(d.fechaFactura)
                    }
                    sortable
                  />
                  <Column
                    header="Cuadratura"
                    body={(d: DiferenciaFacturaRow) => (
                      <div className="fd-tags">
                        {estadoCuadraturaBody(d.estadoFactura)}
                        {estadoPagoBody(d.estadoContable)}
                      </div>
                    )}
                  />
                  <Column
                    header="Facturado"
                    field="montoFacturado"
                    body={moneyBody<DiferenciaFacturaRow>("montoFacturado")}
                    sortable
                  />
                  <Column
                    header="Contabilizado"
                    field="montoContabilizado"
                    body={moneyBody<DiferenciaFacturaRow>("montoContabilizado")}
                    sortable
                  />
                  <Column
                    header="Diferencia"
                    field="diferencia"
                    body={diffBody<DiferenciaFacturaRow>("diferencia")}
                    sortable
                  />
                </DataTable>
              )}
            </TabPanel>

            <TabPanel
              header={`OC sobrefacturadas (${formatNumber(
                diferencias.data?.ordenesSobrefacturadas?.length,
              )})`}
            >
              <div className="fd-note">
                OC cuyo total facturado supera el monto neto de la orden en más
                de {formatMoney(tolerancia)}.
              </div>
              {diferencias.error ? (
                <ChartCard
                  title=""
                  error={diferencias.error}
                  onRetry={diferencias.reload}
                  height={120}
                />
              ) : (
                <DataTable
                  value={diferencias.data?.ordenesSobrefacturadas ?? []}
                  loading={diferencias.loading}
                  size="small"
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sortMode="single"
                  removableSort
                  stripedRows
                  emptyMessage="No hay OC sobrefacturadas"
                  className="fd-table"
                >
                  <Column
                    header="N° OC"
                    field="nroOrdenCompra"
                    sortable
                    bodyClassName="fd-num"
                  />
                  <Column
                    header="Proveedor"
                    field="nombreProveedor"
                    body={proveedorBody}
                    sortable
                  />
                  <Column
                    header="Facturas"
                    field="cantidadFacturas"
                    sortable
                    bodyClassName="fd-num"
                  />
                  <Column
                    header="Monto OC"
                    field="montoOrden"
                    body={moneyBody<OrdenSobrefacturadaRow>("montoOrden")}
                    sortable
                  />
                  <Column
                    header="Facturado"
                    field="montoFacturado"
                    body={moneyBody<OrdenSobrefacturadaRow>("montoFacturado")}
                    sortable
                  />
                  <Column
                    header="Exceso"
                    field="exceso"
                    body={diffBody<OrdenSobrefacturadaRow>("exceso")}
                    sortable
                  />
                </DataTable>
              )}
            </TabPanel>

            <TabPanel
              header={`Nota de débito (${formatNumber(
                ajustesDetalle.notasDebito.length,
              )})`}
            >
              <div className="fd-note">
                Notas de débito (DTE 72), por fecha de{" "}
                {campoFechaLabel.toLowerCase()}.
              </div>
              {ajustesTabla(
                ajustesDetalle.notasDebito,
                "No hay notas de débito para los filtros seleccionados",
              )}
            </TabPanel>
          </TabView>
        </ChartCard>
      </div>
    </div>
  );
}
